import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import QuestionCardNew from './QuestionCardNew';
import { useParams } from 'react-router-dom';
import { trackQuizCompleted, trackLessonCompleted } from '../../../lib/analytics';
import { getVideoLessonProgress } from '../../../lib/videoLessonApi';
import { getTrialProgress, saveTrialProgress } from '../../../lib/trialLessonProgress';
import css from './QuizDesign/quizDesign.module.css';

const QuestionCardExample = ({ onActivityComplete, questions, lessonId: propLessonId, videoLessonId: propVideoLessonId, savedState, onStateChange }) => {
    const location = useLocation();
    const isTrialLesson = location.pathname === '/trial-lesson';
    const activeLessonId = propLessonId;
    const activeVideoLessonId = propVideoLessonId || activeLessonId;

    const [finalScore, setFinalScore] = React.useState(0);
    
    // Load trial progress if available
    const trialQuizProgress = isTrialLesson ? getTrialProgress('quiz') : null;
    const initialSavedState = savedState || trialQuizProgress;
    
    // Якщо є savedState — тест вже починався, пропускаємо intro
    const [showIntro, setShowIntro] = useState(!initialSavedState);
    
    // Create a wrapper for onStateChange that also saves to localStorage
    const handleStateChange = React.useCallback((state) => {
        if (onStateChange) {
            onStateChange(state);
        }
        if (isTrialLesson) {
            saveTrialProgress('quiz', state);
        }
    }, [onStateChange, isTrialLesson]);

    const handleTestComplete = async (results) => { /* без змін */ };

    const handleStartTest = () => {
        setShowIntro(false);
        // Повідомити батька що intro пройдено (щоб при поверненні не показувати знову)
        handleStateChange({ ...initialSavedState, introShown: true });
    };

    if (showIntro) {
        return (
            <div className={css.quizWrap}>
                <div className={css.introScreen}>
                    {/* Icon */}
                    <div className={css.introIcon}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"/>
                            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                            <path d="M12 17h.01"/>
                        </svg>
                    </div>

                    {/* Title & Description */}
                    <h2 className={css.introTitle}>Перевірте розуміння</h2>
                    <p className={css.introDesc}>
                        Пройдіть короткий тест із 9 питань, щоб перевірити, що ви запам'ятали з відео.
                    </p>

                    {/* Stats */}
                    <div className={css.introStats}>
                        <div className={css.introStat}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                            9 питань
                        </div>
                        <div className={css.introStat}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                            3 типи завдань
                        </div>
                        <div className={css.introStat}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                            3–5 хвилин
                        </div>
                    </div>

                    {/* Test Types */}
                    <div className={css.introTypesLabel}>Що буде в тесті</div>
                    <div className={css.introTypes}>

                        <div className={css.introType}>
                            <span className={`${css.introTypeBadge} ${css.badgeWM}`}>Word meaning</span>
                            <p>Оберіть правильний переклад слова.</p>
                            <div className={css.introExample}>"from" означає "з" чи "бо"</div>
                        </div>
                        <div className={css.introType}>
                            <span className={`${css.introTypeBadge} ${css.badgeFB}`}>Fill in the blank</span>
                            <p>Доповніть речення правильним словом із запропонованих варіантів.</p>
                            <div className={css.introExample}>"My ___ is Anna."</div>
                        </div>
                        <div className={css.introType}>
                            <span className={`${css.introTypeBadge} ${css.badgeL}`}>Build the phrase</span>
                            <p>Склади фразу з готових слів.</p>
                            <div className={css.introExample}>«Рахунок, будь ласка» → Check / please / Card</div>
                        </div>

                    </div>

                    {/* Start Button */}
                    <button className={css.introBtn} onClick={handleStartTest}>
                        Почати тест <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                    </button>
                    <div className={css.introNote}>Тест містить різні типи завдань і займе кілька хвилин.</div>
                </div>
            </div>
        );
    }

    return (
        <div>
            {activeVideoLessonId ? (
                <QuestionCardNew
                    videoLessonId={activeVideoLessonId}
                    initialQuestions={questions}
                    onTestComplete={handleTestComplete}
                    onActivityComplete={(score) => {
                        setFinalScore(score);
                        if (onActivityComplete) onActivityComplete(score);
                    }}
                    maxAttempts={2}
                    enabledTestTypes={{ yesNo: true, textVoice: true, dragDrop: false, translationMatch: true }}
                    savedState={initialSavedState}
                    onStateChange={handleStateChange}
                />
            ) : (
                <div>No video lesson ID provided</div>
            )}
        </div>
    );
};

export default QuestionCardExample;